<template>
    <main class="brand-page">
        <section class="section">
            <div class="container">
                <h1 class="title">Brand resources</h1>
                <p>
                    Existing BossDB marks, colors, and visual assets for use in
                    presentations, publications, and project materials.
                </p>
            </div>
        </section>

        <section class="section">
            <div class="container brand-content">
                <div class="brand-section-heading">
                    <h2 class="title is-2">Logo</h2>
                    <p>Use the existing BossDB brain mark without alteration.</p>
                </div>

                <div class="logo-grid">
                    <div class="logo-card logo-card-white">
                        <img :src="logoSvg" alt="BossDB brain logo" />
                    </div>
                    <div class="logo-card logo-card-navy">
                        <img :src="logoSvg" alt="BossDB brain logo on a navy background" />
                    </div>
                    <div class="logo-card logo-card-gray">
                        <img :src="logoSvg" alt="BossDB brain logo on a gray background" />
                    </div>
                </div>
                <div class="buttons asset-links">
                    <a class="button is-primary" :href="logoSvg">Open SVG</a>
                    <a class="button" :href="logoPng">Open PNG</a>
                </div>

                <h3 class="title is-4 secondary-logo-heading">Outlined logo</h3>
                <div class="logo-grid outlined-logo-grid">
                    <div class="logo-card logo-card-white">
                        <img src="/brand/bossdb-logo-outlined.svg" alt="BossDB logo with outer line" />
                    </div>
                    <div class="logo-card logo-card-navy">
                        <img
                            src="/brand/bossdb-logo-outlined.svg"
                            alt="BossDB logo with outer line on a navy background"
                        />
                    </div>
                    <div class="logo-card logo-card-gray">
                        <img
                            src="/brand/bossdb-logo-outlined.svg"
                            alt="BossDB logo with outer line on a gray background"
                        />
                    </div>
                </div>
                <div class="buttons asset-links">
                    <a class="button is-primary" href="/brand/bossdb-logo-outlined.svg">
                        Open SVG
                    </a>
                    <a class="button" href="/brand/bossdb-logo-outlined.png">Open PNG</a>
                </div>

                <h3 class="title is-4 secondary-logo-heading">Secondary logo</h3>
                <div class="secondary-logo-card">
                    <img src="/android-chrome-192x192.png" alt="BossDB secondary logo" />
                </div>
                <div class="buttons asset-links">
                    <a class="button is-primary" href="/android-chrome-192x192.png">
                        Open PNG
                    </a>
                </div>

                <h3 class="title is-4 secondary-logo-heading">Banner</h3>
                <p>BossDB logo and tagline formatted for display materials.</p>
                <div class="buttons asset-links">
                    <a class="button is-primary" href="/brand/bossdb-banner.pdf">Open PDF</a>
                </div>

                <div class="brand-rule"></div>

                <div class="brand-section-heading">
                    <h2 class="title is-2">Colors</h2>
                    <p>Colors taken directly from the BossDB SVG logo.</p>
                </div>
                <div class="color-grid">
                    <article v-for="color in colors" :key="color.hex" class="color-card">
                        <div class="color-swatch" :style="{ backgroundColor: color.hex }"></div>
                        <div class="color-label">
                            <strong>{{ color.name }}</strong>
                            <code>{{ color.hex }}</code>
                        </div>
                    </article>
                </div>

                <div class="brand-rule"></div>

                <div class="brand-section-heading">
                    <h2 class="title is-2">Typography</h2>
                    <p>Font families currently used by bossdb.org.</p>
                </div>
                <div class="type-grid">
                    <article class="type-card">
                        <p class="type-sample type-sample-body">Aa</p>
                        <h3 class="title is-4">Avenir</h3>
                        <p>Primary site typeface, with Helvetica and Arial fallbacks.</p>
                    </article>
                    <article class="type-card">
                        <p class="type-sample type-sample-mark">BossDB</p>
                        <h3 class="title is-4">Crimson Text</h3>
                        <p>Used for the BossDB typemark.</p>
                    </article>
                </div>

                <div class="brand-rule"></div>

                <div class="brand-section-heading">
                    <h2 class="title is-2">Institutional branding</h2>
                    <p>Refer to the institutions themselves for current guidance.</p>
                </div>
                <div class="institution-links">
                    <a href="https://brand.jhu.edu/" target="_blank" rel="noopener noreferrer">
                        Johns Hopkins University brand guidelines
                        <span aria-hidden="true">↗</span>
                    </a>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
import { ASSETS_BASE_URL } from "@/assets.js";

export default {
    data() {
        return {
            logoSvg: `${ASSETS_BASE_URL}bossdb-logo/export/SVG/Logo.svg`,
            logoPng: `${ASSETS_BASE_URL}logo.png`,
            colors: [
                { name: "Navy", hex: "#1E386D" },
                { name: "Blue", hex: "#009DEB" },
                { name: "Magenta", hex: "#E2358B" },
                { name: "Yellow", hex: "#FFF300" },
            ],
        };
    },
};
</script>

<style scoped>
.brand-page {
    background: #fff;
}

.brand-content {
    max-width: 1100px;
}

.brand-section-heading {
    margin-bottom: 2rem;
}

.brand-section-heading .title {
    margin-bottom: 0.4rem;
    color: #1e386d;
}

.brand-rule {
    margin: 5rem 0;
    border-top: 1px solid #d7dce5;
}

.logo-grid,
.type-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem;
}

.type-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.logo-card {
    display: grid;
    min-height: 280px;
    place-items: center;
    border: 1px solid #d7dce5;
}

.logo-card img {
    width: 150px;
    height: 165px;
}

.logo-card-navy {
    background: #1e386d;
    border-color: #1e386d;
}

.logo-card-gray {
    background: #f2f4f7;
}

.asset-links {
    margin-top: 1.5rem;
}

.secondary-logo-heading {
    margin-top: 3rem;
    color: #1e386d;
}

.secondary-logo-card {
    display: grid;
    width: 260px;
    height: 260px;
    place-items: center;
    background: #f2f4f7;
    border: 1px solid #d7dce5;
}

.secondary-logo-card img {
    width: 160px;
}

.outlined-logo-grid img {
    width: 190px;
    max-height: 210px;
}

.color-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 1rem;
}

.color-card {
    overflow: hidden;
    border: 1px solid #d7dce5;
}

.color-swatch {
    height: 150px;
}

.color-label {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    padding: 1rem;
}

.color-label code {
    padding: 0;
    color: #586273;
    background: transparent;
}

.type-card {
    padding: 2rem;
    border: 1px solid #d7dce5;
}

.type-sample {
    min-height: 5rem;
    margin-bottom: 1.5rem;
    color: #1e386d;
    font-size: 4rem;
    line-height: 1;
}

.type-sample-mark {
    font-family: "Crimson Text", serif;
}

.institution-links {
    border-top: 1px solid #1e386d;
}

.institution-links a {
    display: flex;
    justify-content: space-between;
    padding: 1.4rem 0;
    color: #1e386d;
    font-size: 1.2rem;
    font-weight: 700;
    border-bottom: 1px solid #1e386d;
}

.institution-links a:hover {
    color: #007fbf;
}

@media screen and (max-width: 768px) {
    .logo-grid,
    .type-grid {
        grid-template-columns: 1fr;
    }

    .color-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .logo-card {
        min-height: 260px;
    }

    .brand-rule {
        margin: 3.5rem 0;
    }
}
</style>
